import { useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createOpenSeadragon } from "../viewer/Custom-OpenSeadragon";
import styled from "styled-components";
import { useAnnotationInfo } from "../../hooks/useAnnotationInfo";
import { viewerSlidesAtom, ViewerId } from "../../atom";
import { useRecoilState } from "recoil";
import { ViewerContext } from "../../page/Viewer";
import { AnnotationInfoPanel as AnnotationInfoPanelComponent } from "./AnnotationInfoPanel";
import { Button, notification } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { ViewerToggleManager } from "../button";
import { patchUserCustomSetting } from "../../api/user";
import { getSlideMpp } from "../../api/deepzoomAPI";
import { AnnotationPanel } from "./AnnotationPanel";
import { CommentPanel } from "./CommentPanel";
import { InformationArea } from "../consultation";
import ViewerContextMenu from "./ViewerContextMenu";
import { registerViewer, unregisterViewer } from "./viewerRegistry";
import HeatmapOverlay from "./HeatmapOverlay";

interface OSDSlideViewerProps {
  viewerId?: ViewerId;
  filename: string;
  postId: number;
  slideId?: number;
  slideTitle?: string;
  isAnno?: boolean;
  isDemo?: boolean;
  requirePasswordOnEdit?: boolean;
  isReset?: boolean;
  isUserSet?: boolean;
  isSaveSet?: boolean;
  isInfo?: boolean;
  isAnnotationButton?: boolean;
  isCommentButton?: boolean;
  useContextMenu?: boolean;
  loading?: boolean;
  onGetSlideMpp?: (postId: number, filename: string) => Promise<number>;
  onGetAnnotations?: (slideId: number) => Promise<any>;
  onSaveAnnotation?: (slideId: number, path: any, name: string) => Promise<void>;
  onAnnotationClick?: (event: any) => void;
  onDoubleClick?: () => void;
  onActivate?: () => void;
  externalSlide?: any;
  onSlideChange?: (next: any) => void;
}

const SLIDE_EXTENSIONS = [".mrxs", ".ndpi", ".svs"];

const isSlideImageFile = (filename?: string) =>
  !SLIDE_EXTENSIONS.some((extension) => filename?.includes(extension));

const cloneAnnotationInfo = (annoInfo: any) =>
  typeof structuredClone === "function"
    ? structuredClone(annoInfo)
    : JSON.parse(JSON.stringify(annoInfo));

const buildSlideAnnotationPathList = (annotationList?: any[]) => {
  if (!annotationList?.length) {
    return [];
  }

  return annotationList.reduce<any[]>((acc, path) => {
    if (!path?.annoInfo) {
      return acc;
    }

    const annoInfo = cloneAnnotationInfo(path.annoInfo);
    if (annoInfo && Array.isArray(annoInfo) && annoInfo[1]) {
      annoInfo[1].id = `annotation${path.annoId}`;
      annoInfo[1].label = path.content;
      acc.push(annoInfo);
    }

    return acc;
  }, []);
};

const OSDSlideViewer = ({
  viewerId = "left",
  filename,
  postId,
  slideId,
  slideTitle = "",
  isAnno = false,
  isDemo = false,
  requirePasswordOnEdit = false,
  isReset = true,
  isUserSet = true,
  isSaveSet = true,
  isInfo = false,
  isAnnotationButton = true,
  isCommentButton = true,
  useContextMenu = false,
  loading = false,
  onGetSlideMpp,
  onGetAnnotations,
  onSaveAnnotation,
  onAnnotationClick,
  onDoubleClick,
  onActivate,
  externalSlide,
  onSlideChange,
}: OSDSlideViewerProps) => {
  const [viewer, setViewer] = useState<any>(null);
  const viewerRef = useRef<any>(null);
  const [annotations, setAnnotations] = useState<any>(null);
  const [slideComments, setSlideComments] = useState<any[]>([]);
  const [icc, setIcc] = useState("GT450");
  const isControlled = externalSlide !== undefined;
  const [viewerSlides, setViewerSlides] = useRecoilState(viewerSlidesAtom);
  const currentSlide = isControlled ? externalSlide : viewerSlides[viewerId];

  const setCurrentSlide = useCallback(
    (updater: any) => {
      if (isControlled) {
        if (!onSlideChange) return;
        const next =
          typeof updater === "function" ? updater(externalSlide) : updater;
        onSlideChange(next);
        return;
      }
      // atom 기반 듀얼 뷰어 모드
      setViewerSlides((prev) => {
        const current = prev[viewerId];
        const next =
          typeof updater === "function" ? updater(current) : updater;
        return { ...prev, [viewerId]: next };
      });
    },
    [isControlled, externalSlide, onSlideChange, viewerId, setViewerSlides]
  );

  const { userCustomSet, setUserCustomSet } = useContext(ViewerContext);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const slideIdRef = useRef(slideId);
  const onGetAnnotationsRef = useRef(onGetAnnotations);
  const onSaveAnnotationRef = useRef(onSaveAnnotation);
  const onGetSlideMppRef = useRef(onGetSlideMpp);
  const isAnnoRef = useRef(isAnno);
  const useAnnotationRef = useRef(userCustomSet?.useAnnotation);

  // 뷰어별 고유 DOM id
  const osdContainerId = useMemo(
    () => `openseadragon-${viewerId}`,
    [viewerId]
  );

  // 토글 패널 상태
  const [showAnnotationPanel, setShowAnnotationPanel] = useState(false);
  const [showCommentPanel, setShowCommentPanel] = useState(false);
  const [showInformationPanel, setShowInformationPanel] = useState(false);

  // 어노테이션 정보 훅 - 뷰어별로 인스턴스 분리됨 (함수 컴포넌트 인스턴스당 1개)
  const annotationInfo = useAnnotationInfo({
    currentSlide,
    setCurrentSlide,
    requirePasswordOnEdit,
    viewerContainerId: osdContainerId, // 듀얼 뷰어에서 정보창 좌표 계산 기준이 될 컨테이너 id
    onAnnotationsRefresh: (annotations) => {
      setAnnotations(annotations);
      const pathList = buildSlideAnnotationPathList(annotations);
      const v = viewerRef.current;
      if (v && v.areAnnotationsActive?.()) {
        v.setAnnotation(pathList);
      }
    },
  });
  const annotationInfoRef = useRef(annotationInfo);

  useEffect(() => {
    annotationInfoRef.current = annotationInfo;
  }, [annotationInfo]);

  useEffect(() => {
    slideIdRef.current = slideId;
    onGetAnnotationsRef.current = onGetAnnotations;
    onSaveAnnotationRef.current = onSaveAnnotation;
    onGetSlideMppRef.current = onGetSlideMpp;
  }, [slideId, onGetAnnotations, onSaveAnnotation, onGetSlideMpp]);

  // onActivate를 ref로 관리 (stale closure 방지)
  const onActivateRef = useRef(onActivate);
  useEffect(() => {
    onActivateRef.current = onActivate;
  }, [onActivate]);

  useEffect(() => {
    if (!viewer) return;

    const handleCanvasPress = () => {
      onActivateRef.current?.();
    };

    viewer.addHandler("canvas-press", handleCanvasPress);
    viewer.addHandler("canvas-click", handleCanvasPress);

    return () => {
      viewer.removeHandler("canvas-press", handleCanvasPress);
      viewer.removeHandler("canvas-click", handleCanvasPress);
    };
  }, [viewer]);

  useEffect(() => {
    notification.config({
      top: 150,
      maxCount: 3,
    });
  }, []);

  const isImage = isSlideImageFile(currentSlide?.filename);

  const changeColor = () => {
    if (isImage) {
      notification.warning({
        key: `image-icc-notification-warning-${viewerId}`,
        message: "알림",
        description: "이미지 파일은 ICC 프로파일을 적용할 수 없습니다.",
        placement: "topRight",
        duration: 3,
        className: "notification-top-layer",
      });
      return;
    }

    if (icc === "none") setIcc("GT450");
    else setIcc("none");
  };

  const handleAnnotationClickWrapper = (event: any) => {
    annotationInfoRef.current.handleAnnotationClick(event);
  };

  const applyAnnotationResponse = (data: any) => {
    if (data && !Array.isArray(data) && data.annotations) {
      setAnnotations(data.annotations);
      setSlideComments(data.slideComments || []);
      return data.annotations;
    }

    setAnnotations(data);
    return data;
  };

  const getMpp = async () => {
    if (onGetSlideMppRef.current) {
      return onGetSlideMppRef.current(postId, filename || "");
    }

    return getSlideMpp(postId, filename || "");
  };

  useEffect(() => {
    isAnnoRef.current = isAnno;
  }, [isAnno]);

  useEffect(() => {
    useAnnotationRef.current = userCustomSet?.useAnnotation;
  }, [userCustomSet?.useAnnotation]);

  useEffect(() => {
    isImage ? setIcc("none") : setIcc("GT450");
  }, [isImage]);

  // OpenSeadragon 초기화 - 뷰어별 고유 id 사용
  useEffect(() => {
    const newViewer = createOpenSeadragon({
      id: osdContainerId,
      showScalebar: true,
      useScaleBar: userCustomSet?.useScaleBar ?? true,
      useMagnificationBar: userCustomSet?.useMagnificationBar ?? true,
      useRotationSlider: userCustomSet?.useViewerRotationSlider ?? true,
      useFiltering: userCustomSet?.useGammaSlider ?? true,
      showNavigator: userCustomSet?.useViewerThumbnail ?? false,
      useCapture: userCustomSet?.useCaptureButton ?? true,
      isPlainImage: isImage,
      slideTitle,
      saveAnnotation: true,
      isAnno,
      getSlideMpp: () => getMpp(),
      getAnnotations: async () => {
        if (!onGetAnnotationsRef.current || !slideIdRef.current) {
          return null;
        }

        const data = await onGetAnnotationsRef.current(slideIdRef.current);
        const annotations = applyAnnotationResponse(data);
        const list = Array.isArray(annotations)
          ? annotations
          : data?.annotations ?? [];
        return buildSlideAnnotationPathList(list);
      },
      addAnnotation: async (path: any, name: any, rulerValue: any) => {
        if (!onSaveAnnotationRef.current || !slideIdRef.current) {
          return;
        }

        const finalName = rulerValue ? `${name} / (${rulerValue}mm)` : name;
        await onSaveAnnotationRef.current(slideIdRef.current, path, finalName);

        if (!onGetAnnotationsRef.current) {
          return;
        }

        const data = await onGetAnnotationsRef.current(slideIdRef.current);
        applyAnnotationResponse(data);
      },
      annotationClickEvent: handleAnnotationClickWrapper,
      useInvertedZoom: userCustomSet ? userCustomSet.useInvertedZoom : false,
    });

    viewerRef.current = newViewer;
    setViewer(newViewer);
    if (viewerId) registerViewer(viewerId, newViewer);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (viewerId) unregisterViewer(viewerId, newViewer);
      viewerRef.current = null;
      newViewer.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [osdContainerId]);

  useEffect(() => {
    if (!viewer) {
      return;
    }

    viewer.updateRuntimeOptions?.({
      slideTitle,
      useScaleBar: userCustomSet?.useScaleBar ?? true,
      useMagnificationBar: userCustomSet?.useMagnificationBar ?? true,
      useRotationSlider: userCustomSet?.useViewerRotationSlider ?? true,
      useFiltering: userCustomSet?.useGammaSlider ?? true,
      showNavigator: userCustomSet?.useViewerThumbnail ?? false,
      useCapture: userCustomSet?.useCaptureButton ?? true,
      useInvertedZoom: userCustomSet?.useInvertedZoom ?? false,
      gammaValue: userCustomSet?.userGammaValue ?? 1,
    });
    viewer.refreshUiControls?.();
  }, [
    viewer,
    slideTitle,
    userCustomSet?.useScaleBar,
    userCustomSet?.useMagnificationBar,
    userCustomSet?.useViewerRotationSlider,
    userCustomSet?.useGammaSlider,
    userCustomSet?.useViewerThumbnail,
    userCustomSet?.useCaptureButton,
    userCustomSet?.useInvertedZoom,
  ]);

  useEffect(() => {
    if (!viewer || !postId || !filename) {
      return;
    }

    const sourceUrl =
      process.env.REACT_APP_DEEPZOOM_URL + `/${icc}/${postId}/${filename}.dzi`;
    if (viewer.currentSourceUrl !== sourceUrl) {
      viewer.currentSourceUrl = sourceUrl;
      viewer.open(sourceUrl);
    }
  }, [viewer, filename, postId, icc]);

  useEffect(() => {
    if (!viewer) {
      return;
    }

    if (isAnno && !viewer.areAnnotationsActive?.()) {
      viewer.initializeAnnotations?.();
    }

    if (!isAnno && viewer.areAnnotationsActive?.()) {
      viewer.cleanAnnotations?.();
    }
  }, [viewer, isAnno]);

  // 줌/팬 시 어노테이션 정보창 닫기
  useEffect(() => {
    if (viewer) {
      const handleZoomPan = () => {
        if (!annotationInfoRef.current.showEditModal) {
          annotationInfoRef.current.closeAnnotationInfo();
        }
      };

      viewer.addHandler("zoom", handleZoomPan);
      viewer.addHandler("pan", handleZoomPan);

      return () => {
        viewer.removeHandler("zoom", handleZoomPan);
        viewer.removeHandler("pan", handleZoomPan);
      };
    }
  }, [viewer]);

  // 감마값 변경
  useEffect(() => {
    if (!viewer || !userCustomSet?.useGammaSlider) return;
    viewer.setGammaValue?.(userCustomSet?.userGammaValue ?? 1);
  }, [viewer, userCustomSet?.userGammaValue, userCustomSet?.useGammaSlider]);

  // 감마 슬라이더 debounce
  useEffect(() => {
    if (!viewer) return;

    viewer.onGammaInput = (newValue: number) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        setUserCustomSet((prev: any) => {
          const nextSettings =
            prev?.userGammaValue === newValue
              ? prev
              : { ...prev, userGammaValue: newValue };

          if (nextSettings !== prev && !isDemo) {
            patchUserCustomSetting(nextSettings).catch((error) => {
              console.error("Failed to persist gamma value:", error);
            });
          }

          return nextSettings;
        });
      }, 300);
    };

    return () => {
      viewer.onGammaInput = null;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [viewer, setUserCustomSet, isDemo]);

  useEffect(() => {
    if (!viewer || !viewer.areAnnotationsActive?.()) return;
    if (!userCustomSet?.useAnnotation || !isAnno) {
      viewer.cleanAnnotations?.();
    } else {
      const pathList = buildSlideAnnotationPathList(annotations);
      if (pathList && pathList.length > 0) {
        viewer.setAnnotation(pathList);
      }
    }
  }, [viewer, userCustomSet?.useAnnotation, isAnno, annotations]);

  useEffect(() => {
    if (!viewer || !viewer.areAnnotationsActive?.()) return;
    if (!currentSlide?.annotations) return;
    const pathList = buildSlideAnnotationPathList(currentSlide.annotations);
    viewer.setAnnotation(pathList);
    setAnnotations(currentSlide.annotations);
  }, [currentSlide?.annotations]);

  const onReset = () => {
    setUserCustomSet({
      useScaleBar: true,
      useMagnificationBar: true,
      useCaptureButton: true,
      useViewerRotationSlider: true,
      useGammaSlider: true,
      useViewerThumbnail: true,
      useAnnotation: true,
      useICCButton: true,
      userGammaValue: 1,
      useInvertedZoom: false,
    });
    setIcc("none");
  };

  const onSubmit = async () => {
    await patchUserCustomSetting(userCustomSet);
  };

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      {loading && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(255, 255, 255, 1)",
            zIndex: 1000,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            color: "black",
            fontSize: "1.5rem",
          }}
        >
          Loading... <LoadingOutlined style={{ marginLeft: "10px" }} />
        </div>
      )}
      <ViewerContextMenu
        viewer={viewer}
        icc={icc}
        onChangeIcc={changeColor}
        isDemo={isDemo}
        enableContextMenu={useContextMenu}
      >
        <StOpenseadragon
          id={osdContainerId}
          style={{ width: "100%", height: "100%" }}
          $selectedAnnotation={annotationInfo.selectedAnnotation?.annoId}
        />
      </ViewerContextMenu>

      <AnnotationInfoPanelComponent {...annotationInfo} isReadOnly={isDemo} />

      {userCustomSet?.useICCButton && (
        <StyledICCButton onClick={changeColor} $isActive={icc !== "none"}>
          ICC
        </StyledICCButton>
      )}
      <div
        style={{
          position: "absolute",
          bottom: "10px",
          right: "10px",
          display: "flex",
          gap: "4px",
          zIndex: 1000,
        }}
      >
        {!isImage && (
          <HeatmapOverlay viewer={viewer} postId={postId} filename={filename} />
        )}
        {isInfo && (
          <StToggleButton
            $isActive={showInformationPanel}
            onClick={() => setShowInformationPanel(!showInformationPanel)}
          >
            Information
          </StToggleButton>
        )}
        {isAnnotationButton && (
          <StToggleButton
            $isActive={showAnnotationPanel}
            onClick={() => setShowAnnotationPanel(!showAnnotationPanel)}
          >
            Annotation
          </StToggleButton>
        )}
        {isCommentButton && (
          <StToggleButton
            $isActive={showCommentPanel}
            onClick={() => setShowCommentPanel(!showCommentPanel)}
          >
            Comment
          </StToggleButton>
        )}
        {isUserSet && <ViewerToggleManager mode="slide" />}
        {isReset && <Button onClick={onReset}>Reset</Button>}
        {isSaveSet && <Button onClick={onSubmit}>설정 저장</Button>}
      </div>

      {showAnnotationPanel && (
        <AnnotationPanel
          annotations={annotations || []}
          annotationInfo={annotationInfo}
          onClose={() => setShowAnnotationPanel(false)}
          isReadOnly={isDemo}
        />
      )}

      {showCommentPanel && (
        <CommentPanel
          slideId={slideId}
          slideComments={slideComments}
          onCommentsChange={setSlideComments}
          onClose={() => setShowCommentPanel(false)}
        />
      )}

      {isInfo && showInformationPanel && (
        <StInformationPanel>
          <InformationArea />
        </StInformationPanel>
      )}
    </div>
  );
};

interface StToggleButtonProps {
  $isActive: boolean;
}

const StToggleButton = styled.button<StToggleButtonProps>`
  background-color: ${(props) =>
    props.$isActive ? "rgba(24, 144, 255, 0.8)" : "rgba(0, 0, 0, 0.5)"};
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  font-size: 0.6rem;
  padding: 0 0.4rem;

  &:hover {
    background-color: ${(props) =>
      props.$isActive ? "rgba(24, 144, 255, 0.95)" : "rgba(0, 0, 0, 0.75)"};
    border-color: rgba(255, 255, 255, 0.5);
  }
`;

interface StyledICCButtonProps {
  $isActive: boolean;
}

const StyledICCButton = styled(Button)<StyledICCButtonProps>`
  position: absolute;
  background-color: ${(props) =>
    props.$isActive ? "rgba(24, 144, 255, 0.7)" : "rgba(0, 0, 0, 0.5)"} !important;
  color: white !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  border-radius: 4px !important;
  top: 5px;
  right: 30.8em;
  font-size: 0.6rem !important;
  padding: 0 0.4rem;
  height: 1.45rem;

  &:hover {
    background-color: ${(props) =>
      props.$isActive ? "rgba(24, 144, 255, 0.9)" : "rgba(0, 0, 0, 0.8)"} !important;
    border-color: rgba(255, 255, 255, 0.5) !important;
  }
`;

const StInformationPanel = styled.div`
  position: absolute;
  top: 50px;
  right: 10px;
  width: 280px;
  max-height: calc(100% - 60px);
  background-color: rgba(0, 0, 0, 0.45);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
  z-index: 1000;
  overflow: hidden;
`;

interface StOpenseadragonProps {
  $selectedAnnotation?: number;
}

const StOpenseadragon = styled("div")<StOpenseadragonProps>(
  ({ theme, $selectedAnnotation }) => ({
    width: "100%",
    height: "100%",

    "& .annotation": {
      cursor: "pointer",
    },

    [`& #annotation${$selectedAnnotation}`]: {
      strokeWidth: "4 !important",
      filter:
        "brightness(0.85) drop-shadow(0 0 6px currentColor) drop-shadow(0 0 12px currentColor)",

      "& line": {
        strokeWidth: "4 !important",
      },
      "& path": {
        strokeWidth: "4 !important",
      },
    },

    "& .osd-annotation-label-bg": {
      pointerEvents: "none",
    },

    "& .osd-annotation-label": {
      fill: "white !important",
      stroke: "rgba(0, 0, 0, 0.8) !important",
      pointerEvents: "none",
      userSelect: "none",
      paintOrder: "stroke fill",
      fontWeight: "400 !important",
      fontFamily:
        '"Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important',
    },

    '& button[style*="position: absolute"]': {
      backgroundColor: "rgba(0, 0, 0, 0.5) !important",
      color: "white !important",
      border: "1px solid rgba(255, 255, 255, 0.3) !important",
      borderRadius: "4px !important",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15) !important",

      "&:hover": {
        backgroundColor: "rgba(0, 0, 0, 0.8) !important",
        borderColor: "rgba(255, 255, 255, 0.5) !important",
      },
    },

    '& > div[style*="position: absolute"]': {
      backgroundColor: "rgba(0, 0, 0, 0.6) !important",
      padding: "8px 12px !important",
      borderRadius: "4px !important",
      border: "1px solid rgba(255, 255, 255, 0.3) !important",

      '& input[type="range"]': {
        margin: "0 !important",

        "&::-webkit-slider-thumb": {
          WebkitAppearance: "none",
          appearance: "none",
          width: "14px",
          height: "14px",
          borderRadius: "50%",
          background: "rgb(174, 203, 255)",
          cursor: "pointer",
        },

        "&::-moz-range-thumb": {
          width: "14px",
          height: "14px",
          borderRadius: "50%",
          background: "rgb(174, 203, 255)",
          cursor: "pointer",
          border: "none",
        },

        "&::-webkit-slider-runnable-track": {
          background: "rgba(255, 255, 255, 0.9)",
          height: "6px",
          borderRadius: "3px",
        },

        "&::-moz-range-track": {
          background: "rgba(255, 255, 255, 0.9)",
          height: "6px",
          borderRadius: "3px",
        },
      },
    },
  })
);

export default OSDSlideViewer;